import React, {
    useCallback, useState, useContext, useEffect
} from 'react';
import _ from 'lodash';
import { Button } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { useDependencies } from 'gw-portals-dependency-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import metadata from './PaperlessPreferencePage.metadata.json5';
import commonMessages from '../../PolicyChangeCommon.messages';
import messages from './PaperlessPreferencePage.messages';
import styles from '../../PolicyChange.module.scss';

function PaperlessPreferencePage(props) {
    const {
        wizardData, updateWizardData, authHeader, tncTransactionTypesSubTypes,
        user_name: userName, email, termData: terms
    } = props;
    const translator = useContext(TranslatorContext);
    const { submissionVM } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const [isAcknowledged, setAcknowledgement] = useState(false);
    const [previousPaperlessPreference, setPreviousPaperlessPreference] = useState(null);
    const [previousInvoicesPreference, setPreviousInvoicePreference] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingInvoiceChange, setPendingInvoiceChange] = useState(null);
    const [paymentPlan, setPaymentPlan] = useState(null);
    const policyNumber = _.get(submissionVM, 'value.policyNumber');
    const pniEmail = _.get(submissionVM, 'value.baseData_Ext.primaryNamedInsured.emailAddress');
    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    useEffect(() => {
        if (!previousPaperlessPreference && _.get(wizardData, 'submissionVM.value', null)) {
            setPreviousPaperlessPreference(_.get(wizardData, 'submissionVM.value.baseData_Ext.paperlessPreference', false));
        }
    }, [previousPaperlessPreference, wizardData]);

    const getPolicyPaymentPlan = useCallback((policyNo) => {
        BillingService.getPolicyPaymentPlan(
            policyNo,
            authHeader
        ).then((result) => {
            setPaymentPlan(result.paymentPlan);
        });
    }, [BillingService, authHeader]);

    useEffect(() => {
        getPolicyPaymentPlan(policyNumber);
        if (_.isNull(previousInvoicesPreference) && _.get(wizardData, 'submissionVM.value', null)) {
            setPreviousInvoicePreference(_.get(wizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', false));
        }
    }, [getPolicyPaymentPlan, policyNumber, previousInvoicesPreference, wizardData]);

    const handleInvoicesChange = useCallback((newValue) => {
        const currentValue = _.get(wizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', false);
        if (currentValue && !newValue) {
            setPendingInvoiceChange(newValue);
            setShowConfirmationModal(true);
        } else {
            const updatedWizardData = _.cloneDeep(wizardData);
            _.set(updatedWizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', newValue);
            updateWizardData(updatedWizardData);
        }
    }, [wizardData, updateWizardData]);


    const handleConfirmChange = useCallback(() => {
        const updatedWizardData = _.cloneDeep(wizardData);
        _.set(updatedWizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', pendingInvoiceChange);
        updateWizardData(updatedWizardData);
        setShowConfirmationModal(false);
        setPendingInvoiceChange(null);
    }, [wizardData, updateWizardData, pendingInvoiceChange]);

    const handleCancelChange = useCallback(() => {
        setShowConfirmationModal(false);
        setPendingInvoiceChange(null);
    }, []);

    const renderLeafIcon = useCallback(() => {
        return (
            <svg
                width="25"
                height="25"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginLeft: '1px' }}
                className={styles.leafIcon}
            >
                <path
                    d="M10.625 3.57402C7.55469 3.57402 4.95703 5.64287 4.07422 8.49507C5.38672
                        7.81215 6.86719 7.43052 8.4375 7.43052H11.875C12.2188 7.43052 12.5 7.71975 12.5
                        8.07326C12.5 8.42678 12.2188 8.71601 11.875 8.71601H11.25H8.4375C7.78906 8.71601
                        7.16016 8.79234 6.55078 8.93294C5.53906 9.16995 4.60156 9.59176 3.76172 10.1662C1.49609
                        11.7209 0 14.3762 0 17.3931V18.0359C0 18.5702 0.417969 19 0.9375 19C1.45703 19 1.875 18.5702
                        1.875 18.0359V17.3931C1.875 15.4368 2.68359 13.6772 3.97656 12.444C4.75 15.4769 7.43359 17.7145
                        10.625 17.7145H10.6641C15.8242 17.6864 20 12.456 20 6.00843C20 4.29711 19.707 2.67015 19.1758
                        1.20388C19.0742 0.926698 18.6797 0.938749 18.543 1.19987C17.8086 2.61391 16.3555 3.57402 14.6875
                        3.57402H10.625Z"
                    fill="#00A30B"
                />
            </svg>
        );
    }, []);

    const onNext = useCallback(async () => {
        const requestPayload = _.omit(submissionVM.value, 'lobData');
        submissionVM.value = await EndorsementService.saveEndorsement(
            [requestPayload],
            authHeader
        );
        TermsAndConditionUtils.saveTnCAcceptance(
            _.get(terms, 'Version', ''),
            tncTransactionTypesSubTypes,
            'PolicyChange',
            'paperlessPreference',
            authHeader,
            _.get(submissionVM.value, 'jobID'),
            userName,
            email
        ); return wizardData;
    }, [submissionVM, EndorsementService, authHeader, terms, tncTransactionTypesSubTypes,
        userName, email, wizardData]);


    const isFormValid = useCallback(() => {
        const currentPaperlessDocuments = _.get(wizardData, 'submissionVM.value.baseData_Ext.paperlessPreference', null);
        const currentPaperlessInvoices = _.get(wizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', false);
        const hasDocumentChange = currentPaperlessDocuments !== previousPaperlessPreference;
        const hasInvoiceChange = currentPaperlessInvoices !== previousInvoicesPreference;
        const hasAnyChange = hasDocumentChange || hasInvoiceChange;
        return isAcknowledged && hasAnyChange && pniEmail;
    }, [isAcknowledged, wizardData, previousPaperlessPreference,
        previousInvoicesPreference, pniEmail]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        paperlessDocumentsIcon: {
            content: renderLeafIcon()
        },
        paperlessInvoicesIcon: {
            content: renderLeafIcon()
        },
        termsAndConditionsCheckbox: {
            value: isAcknowledged,
            onValueChange: setAcknowledgement
        },
        paperlessPreferenceReviewSection: {
            content: pniEmail ? translator(messages.paperlessReviewsection, { pniEmail })
                : translator(messages.paperlessReviewMsgWithOutEmails, { policyNumber })
        },
        paperlessPreferencesubsection: {
            content: translator(messages.paperlessReviewsubsection)
        },
        paperlessDocumentsSubtitle: {
            content: translator(messages.paperlessPolicyDocumentsSubtitle)
        },
        paperlessInvoicesSubtitle: {
            content: translator(messages.paperlessInvoicesSubtitle)
        },
        paperlessInvoicesCheckbox: {
            value: _.get(wizardData, 'submissionVM.value.baseData_Ext.apwelectronicInvoice', false),
            onValueChange: handleInvoicesChange
        },
        paperlessInvoicesContainer: {
            visible: paymentPlan === 'Monthly APW (12 Pay)',
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <>
            <WizardPage
                onNext={onNext}
                cancelLabel={commonMessages.cancelAllChanges}
                disableNext={!isFormValid()}
                nextLabel={messages.submit}
                previousLabel={messages.back}
            >
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={wizardData}
                    overrideProps={overrideProps}
                    classNameMap={resolvers.resolveClassNameMap}
                    onModelChange={updateWizardData}
                />
                {showConfirmationModal && (
                    <div className={styles.modalOverlay}>
                        <div className={styles.confirmationModal}>
                            <div className={styles.modalHeader}>
                                <h2>{translator(messages.paperlessConfirmyourPreference)}</h2>
                            </div>
                            <div className={styles.modalBody}>
                                <h4>{translator(messages.paperlessturnoffPaperless)}</h4>
                                <p>{translator(messages.popupMessage)}</p>
                                <p>
                                    {translator(messages.paperlessAreYouSure)}
                                </p>
                            </div>
                            <div className={styles.modalFooter}>
                                <Button
                                    className={styles.cancelButton}
                                    onClick={handleCancelChange}
                                >
                                    {translator(messages.paperlessGoBack)}
                                </Button>
                                <Button
                                    className={styles.confirmButton}
                                    onClick={handleConfirmChange}
                                >
                                    {translator(messages.paperlessYes)}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </WizardPage>
        </>
    );
}

PaperlessPreferencePage.propTypes = wizardProps;
export default withAuthenticationContext(withApplicationPropertyContext(PaperlessPreferencePage));
